/** Input Nota FORMAT BARU (brief owner 22 Sep 2026, D1-D10) — bentuk payload
 *  dan badan pratinjau dirakit di SATU tempat.
 *
 *  KENAPA berkas ini ada: tombol "Kirim ke Kasir" dan ringkasan tagihan yang
 *  dilihat kasir harus membicarakan ANGKA YANG SAMA. Kalau pratinjau dirakit
 *  dengan satu cara dan payload simpan dengan cara lain (mis. baris trade-in
 *  kosong ikut di salah satunya), layar bisa berkata "✓ cocok" sementara
 *  server menolak — atau sebaliknya. Keduanya memanggil `susunPayloadNota`.
 *
 *  Sengaja TANPA impor apa pun (murni): fungsi di sini dijalankan sungguhan di
 *  Node oleh `backend/tests/test_layar_nota_brief22.py`, lalu payload hasilnya
 *  dikirim ke server — jadi bentuk yang diuji adalah bentuk yang benar-benar
 *  dikirim layar, bukan salinan di tes.
 *
 *  Angka UANG (tagihan, harus dibayar, cocok/tidak) TIDAK dihitung di sini —
 *  itu milik server (`POST /nota/sales/pratinjau`, rumus `routers/sales.py::
 *  rincian_tagihan`). Layar yang menghitung sendiri dulu membulatkan PPN beda
 *  satu rupiah dan menolak kasir yang benar. */

// Pilihan sub-kolom unit trade-in (brief owner 29 Agu poin 4). Daftar
// TERTUTUP, bukan ketikan bebas: nama unit second dirakit dari kolom-kolom ini
// dan dipakai untuk menentukan harga jualnya kembali — "RESMI" vs "resmi" vs
// "Garansi Resmi" untuk barang yang sama membuat katalog second tidak bisa
// dikelompokkan (pelajaran 85 unit second tanpa kondisi, brief 23 Agu).
// Dipindah dari InputNota.jsx supaya Edit Penjualan (Sales.jsx) memakai daftar
// yang sama, bukan salinan kedua.
export const TI_STATUS = ["RESMI", "BEACUKAI", "UNLOCK", "SIMLOCK", "INTER", "SECOND MINUS"];
export const TI_KELENGKAPAN = ["FULLSET ORI", "MINUS CAS 1 SET", "MINUS KEPALA CAS",
                               "MINUS KABEL", "UNIT ONLY"];

// Cermin `routers/sales.py::TOLERANSI_PEMBAYARAN` (Rp 1). Hanya untuk memberi
// tahu kasir LEBIH AWAL; server tetap validator akhir dengan angka yang sama.
export const TOLERANSI_PEMBAYARAN = 1;

// No HP minimal 10 digit (D8) — cermin `pelanggan.MIN_DIGIT_HP`.
export const MIN_DIGIT_HP = 10;

/** Rakit nama unit trade-in jadi SATU kesatuan (brief poin 5).
 *
 *  Contoh yang diminta owner:
 *      IPHONE 13 128GB / RESMI / STARLIGHT / 85% / FULLSET ORI
 *
 *  Bagian yang kosong DILEWATI, tidak diganti tanda hubung — "IPHONE 13 / / /"
 *  lebih sulit dibaca daripada nama yang lebih pendek. IMEI/SN sengaja TIDAK
 *  ikut: brief memintanya di kolom berikutnya.
 */
export function rakitNamaTradeIn(t) {
  return [t.nama, t.status, t.warna, t.bh, t.kelengkapan]
    .map(x => (x || "").trim())
    .filter(Boolean)
    .join(" / ");
}

export function angka(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function digitHp(hp) {
  return String(hp || "").replace(/\D/g, "");
}

/** 08xx / +628xx / 8xx → 628xx — cermin PERSIS `services/wa_service.py::
 *  normalisasi_hp`, fungsi yang dipakai server memeriksa pemilik DP (D8) dan
 *  kartu pelanggan. Dua normalisasi yang berbeda di layar dan server berarti
 *  layar menawarkan DP yang ujungnya ditolak (aturan 3). */
export function normalisasiHp(hp) {
  const d = digitHp(hp);
  if (!d) return "";
  if (d.startsWith("0")) return "62" + d.slice(1);
  if (d.startsWith("8")) return "62" + d;
  return d;
}

/** D8 di layar: DP hanya bisa dipakai nota dengan No HP yang SAMA. Pencarian
 *  `/down-payment/tersedia` juga mencocokkan AWALAN NAMA (supaya "Budi S" tetap
 *  menemukan DP "Budi Santoso"), jadi hasilnya bisa memuat DP milik No HP
 *  lain — dulu ikut ditawarkan di dropdown tanpa nomornya, selalu ditolak
 *  server, dan pesan tolaknya memperlihatkan nomor orang lain.
 *  Kembalikan `{ cocok, lainHp }`: hanya `cocok` yang boleh jadi pilihan;
 *  `lainHp` cukup dihitung untuk keterangan, tanpa membuka nomornya. */
export function pisahDpMenurutHp(items, noHp) {
  const hp = normalisasiHp(noHp);
  const cocok = [];
  let lainHp = 0;
  for (const d of items || []) {
    if (hp && normalisasiHp(d?.no_hp) === hp) cocok.push(d);
    else lainHp += 1;
  }
  return { cocok, lainHp };
}

/** Diskon NOTA (bukan diskon per baris) yang tersimpan pada penjualan FORMAT
 *  BARU yang dibuka di Edit Penjualan. `diskon_nominal` dokumen yang punya
 *  diskon baris adalah angka TURUNAN (= jumlah diskon baris), bukan diskon
 *  nota — memuatnya sebagai diskon nota membuat baris yang diskonnya dihapus
 *  tetap terpotong. Sebaliknya penjualan yang lahir dari draf LAMA berdiskon
 *  nota membawa diskon itu apa adanya; dulu layar mengirim 0 dan diskonnya
 *  hilang dari jurnal tanpa ada yang menekan apa pun (tinjauan 23 Sep). */
export function diskonNotaTersimpan(sale) {
  if (jumlahDiskonBaris(sale?.items) > 0) return 0;
  return angka(sale?.diskon_nominal);
}

/** `diskon_nominal` yang dikirim Edit Penjualan format baru: jumlah diskon
 *  baris kalau ada (pantulan yang dikenali server), kalau tidak diskon nota
 *  tersimpan apa adanya (server menolak menambah/mengubahnya — D2/D6). */
export function diskonKirimEditRinci(form) {
  const baris = jumlahDiskonBaris(form?.items);
  return baris > 0 ? baris : angka(form?.diskon_nominal);
}

/** Medan `diskon` (= diskon NOTA) badan pratinjau Edit Penjualan format baru.
 *  Dengan diskon baris, diskon nota selalu 0 — server menghitung dari baris. */
export function diskonPratinjauEditRinci(form) {
  return jumlahDiskonBaris(form?.items) > 0 ? 0 : angka(form?.diskon_nominal);
}

/** Metode bernama "Trade-in"/"Tukar Tambah" — cermin `routers/sales.py::
 *  _metode_trade_in`. Trade-in BUKAN baris pembayaran (nilainya sudah dijurnal
 *  lewat bagian Trade-in ke kas penampung); server menolak baris seperti itu,
 *  jadi layar tidak menawarkannya. */
export function metodeTradeIn(nama) {
  const k = String(nama || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return k === "tradein" || k === "tukartambah";
}

/** Buang medan penanda LAYAR (berawalan `_`, mis. `_harga_paket`) dari satu
 *  baris sebelum dikirim — bukan medan dokumen. */
export function buangMedanLayar(obj) {
  const hasil = {};
  Object.keys(obj || {}).forEach((k) => { if (!k.startsWith("_")) hasil[k] = obj[k]; });
  return hasil;
}

/** Patch baris barang saat produk dipilih dari kotak cari. Baris PAKET (SKU
 *  punya resep bundling aktif) mencatat harga yang diisi katalog sebagai
 *  `_harga_paket` — acuan kunci F3 di layar (lihat `hargaPaketTerkunci`).
 *  Patch yang tidak membawa `sku` (mis. memilih IMEI) dikembalikan apa adanya.
 *  `resepMap` = peta sku_bundle (HURUF BESAR) → resep. */
export function patchBarisDenganKunciPaket(patch, resepMap) {
  if (!patch || !Object.prototype.hasOwnProperty.call(patch, "sku")) return patch;
  const sku = String(patch.sku || "").trim().toUpperCase();
  if (sku && resepMap && resepMap[sku]) return { ...patch, _harga_paket: angka(patch.harga) };
  return { ...patch, _harga_paket: undefined };
}

/** F3 (keputusan 23 Sep): harga baris PAKET terkunci = harga jual produk paket
 *  (diisi katalog saat produk dipilih); potongan lewat kolom Diskon produk.
 *  Cermin `routers/sales.py::pastikan_harga_paket_terkunci`:
 *  - produk paket TANPA harga jual (katalog mengisi 0) tidak dikunci — server
 *    juga tidak punya angka acuan, jadi kasir harus bisa mengetik harganya;
 *  - baris bonus tidak dikunci;
 *  - baris yang dimuat dari dokumen tersimpan (tanpa `_harga_paket`) dikunci
 *    pada harga tersimpannya — server tidak menilai ulang baris yang tidak
 *    diubah, jadi mengirimnya kembali apa adanya selalu sah. */
export function hargaPaketTerkunci(item, adaResep) {
  if (!adaResep || item?.is_bonus) return false;
  const acuan = item?._harga_paket;
  return acuan === undefined ? angka(item?.harga) > 0 : angka(acuan) > 0;
}

/** "net_30" → "Net 30" (kartu/detail penjualan & cetakan). */
export function labelTempo(t) {
  const s = String(t || "");
  if (s.startsWith("net_")) return `Net ${s.slice(4)}`;
  return s === "deposit" ? "Deposit" : s;
}

/** Rincian uang dokumen PENJUALAN untuk dicetak (brief 22 Sep B3/D5/D7):
 *  total = NILAI NOTA (barang − diskon + admin + PPN), lalu pembayaran —
 *  baris metode (+ keterangan/No. Kontrak), trade-in sebagai pembayaran, DP —
 *  dan sisa tagihan nota bertempo.
 *
 *  Semua angka datang dari SERVER (`nilai_nota`, `diskon_tampil`/
 *  `diskon_nominal`, `dp_nominal`, `sisa_tempo` dari dokumen piutang); di sini
 *  hanya dipilah jadi baris cetak. `null` = entitas tidak membawa `nilai_nota`
 *  (PO/transfer/retur, atau respons server lama) → cetakan berperilaku persis
 *  seperti sebelum fitur ini ada. Dipakai FakturPrint DAN StrukPrint — satu
 *  sumber, supaya faktur A4 dan struk thermal tidak mencetak angka berbeda. */
export function rincianPenjualanCetak(entity) {
  if (!entity || entity.nilai_nota === null || entity.nilai_nota === undefined) return null;
  const bayar = (entity.payments || []).filter((p) => angka(p?.amount) > 0)
    .map((p) => ({ label: p.method || "-", keterangan: p.keterangan || "", nilai: angka(p.amount), jenis: "metode" }));
  const tradeIn = entity.trade_in
    ? (entity.trade_in_items || []).filter((t) => (t?.nama || "").trim() || angka(t?.harga))
      .map((t) => ({ label: `Trade-in: ${t.nama || "-"}`, keterangan: t.imei ? `IMEI ${t.imei}` : "",
                     nilai: angka(t.harga), jenis: "trade_in" }))
    : [];
  const dp = angka(entity.dp_nominal);
  // Cicilan piutang yang sudah dibayar di Finance sesudah nota (dari dokumen
  // piutang) — tanpa baris ini pembayaran + sisa tidak berjumlah total nota.
  const piutangDibayar = angka(entity.terbayar_piutang);
  const jt = String(entity.tanggal_jatuh_tempo || "");
  return {
    subtotal: angka(entity.total_harga),
    diskon: angka(entity.diskon_tampil ?? entity.diskon_nominal),
    biayaAdmin: angka(entity.biaya_admin_rp),
    total: angka(entity.nilai_nota),
    pembayaran: [
      ...bayar,
      ...tradeIn,
      ...(dp > 0 ? [{ label: "Downpayment (DP)", keterangan: "", nilai: dp, jenis: "dp" }] : []),
      ...(piutangDibayar > 0
        ? [{ label: "Pembayaran piutang", keterangan: "", nilai: piutangDibayar, jenis: "piutang" }] : []),
    ],
    // Dokumen tanpa baris pembayaran (nota LAMA satu metode) hanya menyebut
    // metodenya — nominalnya tidak ditebak dari medan lain di layar.
    metodeTunggal: !bayar.length && !entity.pembayaran_rinci && !entity.tempo_pembayaran
      ? (entity.metode_pembayaran || null) : null,
    tempo: entity.tempo_pembayaran || null,
    // "2026-10-01" → "01/10/2026" (format tanggal dokumen cetak di aplikasi ini).
    jatuhTempo: /^\d{4}-\d{2}-\d{2}$/.test(jt) ? jt.split("-").reverse().join("/") : (jt || null),
    sisa: entity.sisa_tempo === null || entity.sisa_tempo === undefined ? null : angka(entity.sisa_tempo),
  };
}

/** Baris pembayaran kosong. `keterangan` = kolom bebas (No. Kontrak leasing,
 *  D3) — tercetak di faktur, tidak mengubah jurnal. */
export function barisPembayaranBaru(method = "Cash") {
  return { method, amount: 0, keterangan: "" };
}

/** Jumlah diskon PRODUK per baris (D5) — untuk TAMPILAN form saja; angka yang
 *  ditagih tetap dari pratinjau server. */
export function jumlahDiskonBaris(items) {
  return (items || []).reduce((a, it) => a + angka(it?.diskon), 0);
}

/** Pesan galat diskon per baris (0..harga, PIC wajib) — cermin
 *  `routers/sales.py::validasi_diskon_baris` supaya kasir dicegat di layar
 *  dengan pesan yang menyebut barangnya. null = sah. */
export function cekDiskonBaris(items, pic) {
  let ada = false;
  for (const it of items || []) {
    const d = angka(it?.diskon);
    if (!d) continue;
    const nama = (it?.nama_barang || "").trim() || "barang";
    if (d < 0) return `Diskon "${nama}" tidak boleh negatif`;
    if (d > angka(it?.harga) + 0.01) return `Diskon "${nama}" melebihi harganya`;
    ada = true;
  }
  if (ada && !String(pic || "").trim()) {
    return "Ada barang berdiskon — PIC Approval Diskon wajib diisi (siapa yang menyetujui potongannya)";
  }
  return null;
}

/** Payload `POST/PATCH /nota/sales` format baru dari isi form Input Nota.
 *
 *  - `pembayaran_rinci: true` SELALU: baris `payments` adalah satu-satunya
 *    sumber uang; `harga_terima` dikirim 0 (server menolak memaknainya).
 *  - Medan berawalan `_` adalah penanda LAYAR (mis. `_kartu_pelanggan`) —
 *    dibuang, bukan medan nota.
 *  - Trade-in hanya ikut kalau bagiannya dicentang; baris tanpa nama dibuang
 *    (sama untuk pratinjau dan simpan — lihat catatan berkas ini).
 *  - Diskon nota & biaya admin hanya berisi nilai TERSIMPAN draf lama (D2/D4):
 *    form baru tidak punya kolomnya, draf lama mengirimnya ulang apa adanya.
 *  - `total_pembayaran_kasir` kosong = null (bukan 0): 0 adalah angka sah
 *    untuk nota bertempo yang belum dibayar sepeser pun (D2).
 *  `fmtRp` hanya untuk teks ringkasan `trade_in_note` (kompat lama). */
export function susunPayloadNota(form, { fmtRp } = {}) {
  const fmt = fmtRp || ((n) => String(Math.round(angka(n))));
  const items = (form.items || []).map((it) => {
    const { komponen, ...rest } = buangMedanLayar(it);
    const baris = { ...rest, harga: angka(it.harga), diskon: angka(it.diskon) };
    // Kontrak B4: server mengharap `komponen: [{sku, qty, imei}]` ringkas —
    // field tampilan (`nama`/`has_imei`) tidak ikut. Baris non-paket TIDAK
    // mengirim `komponen` sama sekali (bukan array kosong).
    if (!komponen?.length) return baris;
    return {
      ...baris,
      komponen: komponen.map((k) => ({ sku: k.sku, qty: k.qty, imei: (k.imei || "").trim() || null })),
    };
  });
  const payments = (form.payments || []).map((p) => ({
    method: String(p.method || "").trim(),
    amount: angka(p.amount),
    keterangan: String(p.keterangan || "").trim() || null,
  }));
  const bernominal = payments.filter((p) => p.amount > 0);
  const tiAktif = !!form.trade_in;
  // Nama yang DISIMPAN adalah bentuk tergabungnya (brief poin 5); sub-kolomnya
  // TETAP ikut dikirim supaya bisa dibaca terpisah nanti.
  const tiItems = tiAktif
    ? (form.trade_in_items || [])
      .filter((t) => (t.nama || "").trim())
      .map((t) => ({ ...t, harga: angka(t.harga), nama: rakitNamaTradeIn(t), imei: (t.imei || "").trim() }))
    : null;
  const payload = {};
  Object.keys(form || {}).forEach((k) => { if (!k.startsWith("_")) payload[k] = form[k]; });
  const kosong = (v) => v === "" || v === null || v === undefined;
  return {
    ...payload,
    items,
    pembayaran_rinci: true,
    payments,
    is_split: bernominal.length > 1,
    metode_pembayaran: bernominal[0]?.method || payments[0]?.method || "Cash",
    harga_terima: 0,
    // Satu nota = satu gudang (kontrak G1) — "" -> null, server menurunkan sendiri.
    warehouse_id: form.warehouse_id || null,
    trade_in: tiAktif,
    trade_in_items: tiItems,
    trade_in_note: tiAktif
      ? tiItems.map((t) => `${t.nama}${t.imei ? ` [IMEI ${t.imei}]` : ""}${t.harga ? ` (${fmt(t.harga)})` : ""}`).join(", ")
      : "",
    // Kosong dikirim sebagai null — server membedakan "tanpa tempo" dari nilai
    // yang tidak dikenal.
    tempo_pembayaran: form.tempo_pembayaran || null,
    pic_approval_piutang: String(form.pic_approval_piutang || "").trim() || null,
    diskon: angka(form.diskon),
    diskon_pic_approval: String(form.diskon_pic_approval || "").trim() || null,
    biaya_admin_mode: form.biaya_admin_mode || null,
    biaya_admin_nilai: angka(form.biaya_admin_nilai),
    dp_id: form.dp_id || null,
    // KOSONG dikirim null (bukan 0): server membaca None sebagai "pakai seluruh
    // sisa DP (dibatasi tagihan − trade-in)" dan menolak nol.
    dp_nominal: form.dp_id ? (kosong(String(form.dp_nominal ?? "").trim()) ? null : angka(form.dp_nominal)) : null,
    refund_nama_bank: String(form.refund_nama_bank || "").trim() || null,
    refund_no_rekening: String(form.refund_no_rekening || "").trim() || null,
    refund_atas_nama: String(form.refund_atas_nama || "").trim() || null,
    kode_antrian: String(form.kode_antrian || "").trim() || null,
    total_pembayaran_kasir: kosong(form.total_pembayaran_kasir) ? null : angka(form.total_pembayaran_kasir),
  };
}

/** Badan `POST /nota/sales/pratinjau` — diambil DARI payload simpan, bukan
 *  dirakit terpisah (lihat catatan berkas ini). */
export function bodyPratinjauNota(payload) {
  return {
    items: payload.items || [],
    kena_ppn: !!payload.kena_ppn,
    trade_in_items: payload.trade_in_items || null,
    dp_id: payload.dp_id || null,
    dp_nominal: payload.dp_nominal ?? null,
    no_hp: payload.no_hp || "",
    payments: payload.payments || [],
    jenis_pelanggan: payload.jenis_pelanggan || null,
    tempo_pembayaran: payload.tempo_pembayaran || null,
    pic_approval_piutang: payload.pic_approval_piutang || null,
    diskon: angka(payload.diskon),
    biaya_admin_mode: payload.biaya_admin_mode || null,
    biaya_admin_nilai: angka(payload.biaya_admin_nilai),
  };
}

/** Selisih "Total Pembayaran" ketikan kasir terhadap jumlah baris pembayaran
 *  (D1/D6). null = kasir belum mengetik apa pun (bukan "0"). */
export function selisihTotalKasir(total, jumlahBaris) {
  if (total === "" || total === null || total === undefined) return null;
  return Math.round((angka(total) - angka(jumlahBaris)) * 100) / 100;
}

export function totalKasirCocok(total, jumlahBaris) {
  const s = selisihTotalKasir(total, jumlahBaris);
  return s !== null && Math.abs(s) <= TOLERANSI_PEMBAYARAN;
}
